import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../api/client.js';
import type { PosBill } from '../../api/pos-types.js';
import { Chip, QueryState, type TabProps } from './common.js';
import { actorLabel, formatDateTime, formatRupees, humanize } from './format.js';
import { SETTLEMENT_STATE_TEXT, textOr } from './labels.js';

export function BillsTab({ orgId, outletId }: TabProps): JSX.Element {
  const [date, setDate] = useState('');
  const [flagged, setFlagged] = useState(false);
  const bills = useQuery({
    queryKey: ['pos-bills', orgId, outletId, date, flagged],
    queryFn: () => {
      const p = new URLSearchParams({ flagged: String(flagged) });
      if (date) p.set('businessDate', date);
      return apiFetch<{ bills: PosBill[] }>(
        `/organizations/${orgId}/outlets/${outletId}/pos/bills?${p.toString()}`,
      );
    },
  });

  return (
    <section aria-labelledby="bills-heading">
      <h2 id="bills-heading">Bills</h2>
      <div className="filter-bar">
        <label className="inline-field">
          Business date <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="inline-field">
          <input type="checkbox" checked={flagged} onChange={(e) => setFlagged(e.target.checked)} />{' '}
          Only bills that need a look
        </label>
      </div>
      <QueryState loading={bills.isLoading} error={bills.error} />
      {bills.data && bills.data.bills.length === 0 ? (
        <p className="muted">
          {flagged ? 'No flagged bills.' : 'No bills yet for this selection.'}
        </p>
      ) : null}
      {bills.data && bills.data.bills.length > 0 ? (
        <div className="table-wrap">
          <table className="pos-table">
            <thead>
              <tr>
                <th scope="col">Invoice</th>
                <th scope="col">Finalized</th>
                <th scope="col">Where</th>
                <th scope="col">Billed by</th>
                <th scope="col">Total</th>
                <th scope="col">Paid</th>
                <th scope="col">State</th>
                <th scope="col">Needs a look</th>
              </tr>
            </thead>
            <tbody>
              {bills.data.bills.map((b) => (
                <tr key={b.id}>
                  <th scope="row">
                    <code>{b.invoiceNo}</code>
                    {b.isCorrection ? <Chip tone="warn">correction</Chip> : null}
                  </th>
                  <td>{formatDateTime(b.finalizedAt)}</td>
                  <td>
                    {b.channel.replace(/_/g, ' ')}
                    {b.tableCode ? ` · ${b.tableCode}` : ''}
                  </td>
                  <td>{actorLabel(b.actorRef, b.actorName)}</td>
                  <td>{formatRupees(b.totalPaise)}</td>
                  <td>{formatRupees(b.coveredPaise)}</td>
                  <td>
                    {b.lifecycleState === 'voided'
                      ? 'Voided'
                      : textOr(SETTLEMENT_STATE_TEXT, b.settlementState)}
                  </td>
                  <td>
                    {b.flags.length === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      b.flags.map((f) => (
                        <Chip key={f} tone="warn">
                          {humanize(f)}
                        </Chip>
                      ))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
